import { useCallback, useEffect, useRef, useState } from "react";
import { api, broadcast, getChannel } from "../lib/api";
import { BROADCAST_EVENTS, DEFAULT_CONFIG } from "../lib/constants";
import { useAuth } from "../context/AuthContext";

export const useQueueConfig = ({ refetchKey } = {}) => {
  const { user } = useAuth();
  const [config, setConfigState] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);

  const fetchConfig = useCallback(async () => {
    if (!user?.company_id) {
      setConfigState(DEFAULT_CONFIG);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/config");
      setConfigState({ ...DEFAULT_CONFIG, ...data.config });
    } catch {
      setConfigState(DEFAULT_CONFIG);
    } finally {
      setLoading(false);
    }
  }, [user?.company_id]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // External trigger: refetch when refetchKey changes (e.g. config_updated_at from /state poll).
  // Skips the initial render to avoid double-fetch on mount.
  const lastKeyRef = useRef(undefined);
  useEffect(() => {
    if (refetchKey === undefined || refetchKey === null) return;
    if (lastKeyRef.current === undefined) {
      lastKeyRef.current = refetchKey;
      return;
    }
    if (lastKeyRef.current !== refetchKey) {
      lastKeyRef.current = refetchKey;
      fetchConfig();
    }
  }, [refetchKey, fetchConfig]);

  // Listen for config changes from other tabs in same browser
  useEffect(() => {
    const ch = getChannel();
    if (!ch) return;
    const handler = (e) => {
      if (e.data?.type === BROADCAST_EVENTS.CONFIG_UPDATED) fetchConfig();
    };
    ch.addEventListener("message", handler);
    return () => ch.removeEventListener("message", handler);
  }, [fetchConfig]);

  const setConfig = useCallback(
    async (updater) => {
      const next = typeof updater === "function" ? updater(config) : { ...config, ...updater };
      setConfigState(next);
      try {
        await api.put("/config", { config: next });
        broadcast(BROADCAST_EVENTS.CONFIG_UPDATED);
      } catch (err) {
        // optimistic UI; let caller handle errors via try/catch on its own
      }
    },
    [config]
  );

  return { config, setConfig, loading, refresh: fetchConfig };
};
